import { useRef } from 'react';

import {
  calculateDotDistances,
  calculateDotOffset,
} from '../ScrollingCarousel.helpers';

type UseCarouselDotsProps = {
  childVisibility: boolean[];
  firstVisibleIndex: number;
  lastVisibleIndex: number;
  anyItemsVisible: boolean;
  numberOfDots: number;
};

type UseCarouselDotsReturn = {
  dotOffset: React.MutableRefObject<number>;
  dotDistances: number[];
  dotWidth: number;
};

/**
 * Hook for managing carousel dots positioning and visibility
 */
export const useCarouselDots = ({
  childVisibility,
  firstVisibleIndex,
  lastVisibleIndex,
  anyItemsVisible,
  numberOfDots,
}: UseCarouselDotsProps): UseCarouselDotsReturn => {
  const dotOffset = useRef(0);
  const dotWidth = 12;
  const totalDots = childVisibility.length;

  // Try to keep this dot visually in the middle
  const dotToCenterIndex = Math.round(
    (firstVisibleIndex + lastVisibleIndex) / 2
  );

  // Allow this many dots at the start and the end before we start moving
  const dotMovementNeeded = Math.round(numberOfDots / 2) + 1;

  // Are we near the start or end?
  const centerDotNearStart = dotToCenterIndex < dotMovementNeeded - 1;
  const centerDotNearEnd = dotToCenterIndex > totalDots - dotMovementNeeded;

  // Only move the offset if we are sure we have the correct data because
  // it can seem as if nothing is visible when the user is scrolling
  if (anyItemsVisible) {
    // It's nice to keep the dots in place if we are near the beginning or end
    if (centerDotNearStart) {
      // Just show the start of the dot list
      dotOffset.current = 0;
    } else if (centerDotNearEnd) {
      // Just show the end of the dots list
      dotOffset.current = dotWidth * (totalDots - numberOfDots);
    } else {
      // Follow the `dotToCenter`
      dotOffset.current = calculateDotOffset(
        dotToCenterIndex,
        2,
        dotWidth,
        dotMovementNeeded
      );
    }
  }

  const dotDistances = calculateDotDistances(childVisibility, dotToCenterIndex);

  return {
    dotOffset,
    dotDistances,
    dotWidth,
  };
};
